import { skipToken, useQuery } from '@tanstack/react-query'

import { TechnicalError } from '@orus.eu/error'

import { useTRPC } from '../../client.ts'
import { isBrokerClientFromContracts, isBrokerClientFromOrganizations } from '../is-broker-client.ts'
import { usePartnerOrganization } from '../use-permissions.ts'

import { useSession } from '#/lib/session.ts'
import type { FrontOfficeQueryOptions } from '#/shared/front-office-query-options.ts'

export type UseIsBrokerClientArgs =
  | {
      context: 'customer'
      queryOptions?: FrontOfficeQueryOptions
    }
  | {
      context: 'backoffice'
      userId: string
      queryOptions?: FrontOfficeQueryOptions
    }

type UseIsBrokerClientResult = {
  isBrokerClient: boolean
  isPending: boolean
}

/**
 * Whether a user has at least one signed contract owned by a broker org (non-Orus, not managed by Orus).
 *
 * Customer sessions without `contract.read` skip the customer query.
 * Backoffice views pass `context: 'backoffice'` with the target `userId`.
 */
export function useIsBrokerClient(args: UseIsBrokerClientArgs): UseIsBrokerClientResult {
  const session = useSession()
  const currentUserId = session.user?.id
  const requestingOrganization = usePartnerOrganization() ?? 'orus'
  const trpc = useTRPC()

  switch (args.context) {
    case 'customer':
      break
    case 'backoffice':
      if (args.userId === undefined) {
        throw new TechnicalError(
          'Backoffice usage should resolve broker client status for a user but no user ID was provided',
          {
            context: { userId: args.userId, currentUserId },
          },
        )
      }
      break
  }

  const { data: contracts, isPending: isContractsPending } = useQuery(
    trpc.contracts.listMyContracts.queryOptions(undefined, {
      ...args.queryOptions,
      enabled: args.context === 'customer' && !!currentUserId && session.allPermissions.includes('contract.read'),
    }),
  )

  const { data: organizations, isPending: isOrganizationsPending } = useQuery(
    trpc.users.getUserOrganizationsV2.queryOptions(
      args.context === 'backoffice' ? { userId: args.userId, requestingOrganization } : skipToken,
      {
        ...args.queryOptions,
        enabled: args.context === 'backoffice',
      },
    ),
  )

  switch (args.context) {
    case 'customer':
      // Client that make their first quote in the funnel
      if (currentUserId === undefined) {
        return { isBrokerClient: false, isPending: false }
      }
      if (isContractsPending) {
        return { isBrokerClient: false, isPending: true }
      }
      return { isBrokerClient: isBrokerClientFromContracts(contracts ?? []), isPending: false }
    case 'backoffice':
      if (isOrganizationsPending) {
        return { isBrokerClient: false, isPending: true }
      }
      return { isBrokerClient: isBrokerClientFromOrganizations(organizations ?? []), isPending: false }
  }
}
